import { Wallet, TrendingUp, Target, BarChart3 } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { fmtEUR, fmtPct } from "../lib/calculations";
import type { ComputedResults } from "../lib/types";

interface Props {
  results: ComputedResults;
  hasCredit: boolean;
}

export function MetricsGrid({ results, hasCredit }: Props) {
  const cf = results.cashFlowMensuel;
  const cfBadge =
    cf > 100
      ? { label: "Positif", classes: "bg-success/10 text-success border-success/30" }
      : cf >= -100
      ? { label: "Neutre", classes: "bg-warning/10 text-warning border-warning/30" }
      : { label: "Négatif", classes: "bg-danger/10 text-danger border-danger/30" };

  const rn = results.rendementNet;
  const rnBadge =
    rn < 3
      ? { label: "Faible", classes: "bg-danger/10 text-danger border-danger/30" }
      : rn < 5
      ? { label: "Moyen", classes: "bg-warning/10 text-warning border-warning/30" }
      : rn < 7
      ? { label: "Bon", classes: "bg-success/10 text-success border-success/30" }
      : { label: "Excellent", classes: "bg-success/10 text-success border-success/30" };

  const showCfNudge = cf < 0 && hasCredit;
  const scrollToAffiliates = () => {
    if (typeof document === "undefined") return;
    document
      .getElementById("affiliate-partners")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section className="mx-auto max-w-6xl px-6 py-10 lg:px-12">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Card
          icon={Wallet}
          iconTone="text-success bg-success/10"
          label="Cash-flow mensuel"
          value={fmtEUR(cf, { sign: true })}
          valueColor={cf > 0 ? "text-success" : cf < 0 ? "text-danger" : "text-foreground"}
          badge={cfBadge}
          desc="Ce que le bien vous met en poche chaque mois après toutes charges et crédit."
          extra={
            showCfNudge ? (
              <button
                type="button"
                onClick={scrollToAffiliates}
                className="mt-3 inline-flex text-left text-xs font-medium text-primary underline-offset-2 hover:underline"
              >
                → Réduisez votre mensualité en optimisant votre crédit
              </button>
            ) : null
          }
        />
        <Card
          icon={TrendingUp}
          iconTone="text-primary bg-primary/10"
          label="Rendement net"
          value={fmtPct(rn)}
          valueColor="text-foreground"
          badge={rnBadge}
          desc="Vs moyenne du marché (4,5%) et Livret A (3%)."
        />
        {hasCredit && results.roiApport !== null ? (
          <Card
            icon={Target}
            iconTone="text-primary bg-primary/10"
            label="Rentabilité sur apport (ROI)"
            value={fmtPct(results.roiApport)}
            valueColor="text-primary"
            desc={`Grâce à l'effet de levier du crédit, votre apport génère ${fmtPct(
              results.roiApport
            )} par an.`}
          />
        ) : (
          <Card
            icon={BarChart3}
            iconTone="text-foreground bg-muted"
            label="Rendement brut"
            value={fmtPct(results.rendementBrut)}
            valueColor="text-foreground"
            desc="Loyers annuels divisés par l'investissement total. Sans tenir compte des charges."
          />
        )}
      </div>
    </section>
  );
}

function Card({
  icon: Icon,
  iconTone,
  label,
  value,
  valueColor,
  badge,
  desc,
  extra,
}: {
  icon: LucideIcon;
  iconTone: string;
  label: string;
  value: string;
  valueColor: string;
  badge?: { label: string; classes: string };
  desc: string;
  extra?: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-6 shadow-sm transition hover:shadow-md">
      <div className="flex items-start justify-between">
        <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${iconTone}`}>
          <Icon className="h-5 w-5" aria-hidden />
        </span>
        {badge && (
          <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${badge.classes}`}>
            {badge.label}
          </span>
        )}
      </div>
      <p className="mt-3 text-sm font-medium text-muted-foreground">{label}</p>
      <p className={`mt-1 text-3xl font-extrabold tabular ${valueColor}`}>{value}</p>
      <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{desc}</p>
      {extra}
    </div>
  );
}
